var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { Icon, Text, Stack, StackChildren, InlineChildren, } from '@doordash/design-language';
import Loading from '@doordash/component-loading';
import Button from '@doordash/component-button';
import { useToast } from '@doordash/component-toast';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import GroupsList from './groups_list';
import { AUTO_OPEN_ADD_MEMBER_QUERY_STRING } from './values';
import { CreateGroupButtonContainer } from './groups_styles';
import { AdminPageSubpageContentContainer } from '../AdminPage/AdminPageStyles';
import CreateGroupModal from './modals/create_group_modal';
import DeleteGroupModal from './modals/delete_group_modal';
import track from '../../utils/track';
import { fetchTeamAccountGroups } from './groups_slice';
import { GroupActions } from '../../constants/actions';
import { getStatusForAction, getTeamAccountGroups } from './groups_selector';
import { FetchStatus, INITIAL_FETCH_STATUS } from '../../constants/fetch_status';
import { displayAddGroupMembersSuccessToast } from '../GroupsMembers/modals/add_members_to_group_modal';
import AdminPageEmptyState from '../admin_page_empty_state';
import { GroupPageTestId } from '../testId';
import { GroupsEvents } from '../../constants/events';
import { PORTAL_ROUTE_PREFIX } from '../../constants/values';
import BulkAddMembersToGroupModal from '../GroupsMembers/modals/bulk_add_members_to_group_modal';
import { getSelectedTeamAccountContent } from '../../selectors/uiSelector';
import { useTeamAccountConfigurationContext } from '../TeamAccountPlanDetails/TeamAccountConfigurationContext';
import { getMembersPagination as getTeamAccountMembersPagination } from '../../selectors/members_selector';
import { fetchMembers } from '../members_slice';
export var Groups = function (_a) {
    var teamAccountId = _a.teamAccountId;
    var dispatch = useDispatch();
    var displayToast = useToast().displayToast;
    var history = useHistory();
    var teamAccount = useSelector(getSelectedTeamAccountContent);
    var _b = useTeamAccountConfigurationContext(), maxMembersLimit = _b.maxMembersLimit, hasMaxMembersLimit = _b.hasMaxMembersLimit;
    var totalMembersCount = useSelector(getTeamAccountMembersPagination).totalCount;
    var statusForAction = useSelector(getStatusForAction);
    var groupsList = useSelector(getTeamAccountGroups);
    var _c = __read(React.useState(false), 2), isGroupModalOpen = _c[0], setIsGroupModalOpen = _c[1];
    var _d = __read(React.useState(null), 2), deleteModalProps = _d[0], setDeleteModalProps = _d[1];
    var _e = __read(React.useState(null), 2), editModalProps = _e[0], setEditModalProps = _e[1];
    var fetchGroups = React.useCallback(function () { return dispatch(fetchTeamAccountGroups({ teamAccountId: teamAccountId })); }, [dispatch, teamAccountId]);
    var fetchTeamAccountMembersCount = React.useCallback(function () {
        return dispatch(fetchMembers({ teamAccountId: teamAccountId, pageNumber: '1', pageSize: 1 }));
    }, [dispatch, teamAccountId]);
    React.useEffect(function () {
        track(GroupsEvents.GROUPS);
        fetchGroups();
    }, [fetchGroups]);
    React.useEffect(function () {
        // when an account has a limit and the total members count is not yet retrieved
        if (hasMaxMembersLimit && !totalMembersCount) {
            fetchTeamAccountMembersCount();
        }
    }, [fetchTeamAccountMembersCount, hasMaxMembersLimit, totalMembersCount]);
    var isFetchStatusPending = function () {
        var _a;
        return [INITIAL_FETCH_STATUS, FetchStatus.PENDING].includes((_a = statusForAction[GroupActions.FETCH_TEAM_ACCOUNT_GROUPS]) === null || _a === void 0 ? void 0 : _a.status);
    };
    var isFetchStatusError = function () {
        var _a;
        return [FetchStatus.INVALID_STATUS, FetchStatus.REJECTED].includes((_a = statusForAction[GroupActions.FETCH_TEAM_ACCOUNT_GROUPS]) === null || _a === void 0 ? void 0 : _a.status);
    };
    var _f = __read(React.useState(null), 2), selectedGroupToAddMembers = _f[0], setSelectedGroupToAddMembers = _f[1];
    var onCloseModal = React.useCallback(function () { return setSelectedGroupToAddMembers(null); }, [setSelectedGroupToAddMembers]);
    var renderLoadingOrError = function (isError) {
        if (isError) {
            return (React.createElement(AdminPageEmptyState, { icon: React.createElement(Icon, { type: Icon.Types.WarningTriangleLine }), title: "Error loading groups", subtitle: "" }));
        }
        return (React.createElement(Loading, { size: Loading.Sizes.Medium, color: Loading.Colors.SystemGrey30 }));
    };
    var displaySuccessMessage = function (group, options) {
        if (options === void 0) { options = { memberCount: 0 }; }
        displayAddGroupMembersSuccessToast(group, options, displayToast);
    };
    var redirectToCreatedGroup = function (groupId) {
        history.push({
            pathname: PORTAL_ROUTE_PREFIX + "/admin/" + teamAccountId + "/groups/" + groupId,
            search: "?" + AUTO_OPEN_ADD_MEMBER_QUERY_STRING + "=true",
        });
    };
    return (React.createElement(AdminPageSubpageContentContainer, null,
        React.createElement(Stack, { size: Stack.Sizes.XLarge },
            React.createElement(InlineChildren, { justifyContent: InlineChildren.Justification.SpaceBetween, alignItems: InlineChildren.Alignment.Center },
                React.createElement(StackChildren, { size: Stack.Sizes.XxxSmall },
                    React.createElement(Text, { color: Text.Colors.TextPrimary, styles: Text.Styles.SectionTitleLarge, weight: Text.Weights.Medium }, "All Groups"),
                    React.createElement(Text, { color: Text.Colors.TextTertiary },
                        groupsList.length,
                        " groups")),
                React.createElement(CreateGroupButtonContainer, null,
                    React.createElement(Button, { type: Button.Types.PrimaryPill, onClick: function () {
                            setIsGroupModalOpen(true);
                            track(GroupsEvents.CLICK_CREATE_GROUP);
                        }, "data-testid": GroupPageTestId.CreateNewGroupButton }, "Create New Group"),
                    isGroupModalOpen && (React.createElement(CreateGroupModal, { teamAccountId: teamAccountId, group: editModalProps === null || editModalProps === void 0 ? void 0 : editModalProps.group, onGroupCreated: function (groupId) {
                            fetchGroups(); // refresh groups list on create
                            redirectToCreatedGroup(groupId);
                        }, onClose: function () {
                            setIsGroupModalOpen(false);
                            setEditModalProps(null);
                        } })),
                    deleteModalProps && (React.createElement(DeleteGroupModal, { group: deleteModalProps.group, onGroupDeleted: function () {
                            fetchGroups(); // refresh groups list on delete
                        }, onClose: function () { return setDeleteModalProps(null); } }))))),
        isFetchStatusPending() || isFetchStatusError() ? (renderLoadingOrError(isFetchStatusError())) : (React.createElement(React.Fragment, null,
            React.createElement(GroupsList, { teamAccountId: teamAccountId, groups: groupsList, onDeleteGroupClick: function (group) { return setDeleteModalProps({ group: group }); }, onEditGroupClick: function (group) {
                    setEditModalProps({ group: group });
                    setIsGroupModalOpen(true);
                }, onAddMembersClick: function (group) { return setSelectedGroupToAddMembers(group); } }),
            React.createElement(BulkAddMembersToGroupModal, { teamAccount: teamAccount, teamAccountPlanMembersInfo: {
                    maxMembersLimit: (maxMembersLimit === null || maxMembersLimit === void 0 ? void 0 : maxMembersLimit.limit) || null,
                    totalMembersCount: totalMembersCount,
                }, group: selectedGroupToAddMembers, onClose: onCloseModal, onSuccess: displaySuccessMessage })))));
};
export default Groups;
